import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type AssetStatus,
  STATUS_VARIANT,
  formatLifecycleDate,
  statusReasonKey,
} from "./assetStatus";

type Props = {
  /** `null` while the latest operating state is still being fetched. */
  status: AssetStatus | null;
};

/** Status badge with reason and last-uplink time, shown under the asset title (tri-server #1173). */
export default function AssetStatusHeader({ status }: Props) {
  const { t } = useTranslation();

  if (!status) {
    return <Skeleton className="mt-2 h-6 w-40" data-testid="asset-status-loading" />;
  }

  const reason = t(`assetStatus.reason.${statusReasonKey(status)}`);
  const since = status.since ? formatLifecycleDate(status.since, true) : null;

  return (
    <div className="mt-2 flex min-w-0 flex-col gap-1" data-testid="asset-status">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <span className="sr-only">{t("assetStatus.label")}</span>
        <Badge variant={STATUS_VARIANT[status.kind]} data-testid="asset-status-badge">
          {t(`assetStatus.kind.${status.kind}`)}
        </Badge>
        <span className="min-w-0 break-words text-sm text-muted-foreground">
          {reason}
          {since ? ` ${t("assetStatus.since", { date: since })}` : ""}
        </span>
      </div>
      <div className="text-sm">
        <span className="text-muted-foreground">{t("assetStatus.lastUplink")}: </span>
        <span className="font-medium">{formatLifecycleDate(status.lastUplinkAt, true)}</span>
      </div>
    </div>
  );
}
